import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { chatResetMessage } from '../../../Actions/GroupChatMessageActions';
import { ActiveChatAction } from '../../../Actions/RecentChatActions';
import { formatGroupIdToJid, formatUserIdToJid } from '../../../Helpers/Chat/User';
import { isAppOnline } from '../../../Helpers/Utility';
import { T, TOAST } from '../../../localization/toastKeys';
import { useLanguage } from '../../../localization/useLanguage';
import ErrorBoundary from '../../../ui/ErrorBoundary';
import ContactListDialog from '../../../Components/WebChat/Common/ContactList/ContactListDialog';
import NewContactDialog from '../NewContactDialog';
import NewGroupDialog from '../NewGroupDialog';
import { CREATE_CHAT_FLOW } from '../constants';
import { toRosterEntry } from '../../../Components/WebChat/Common/ContactList/contactHelpers';
import { showOfflineError } from '../../../ui/DialogFrame/dialogToast';

const NEW_CONTACT = 'newContact';

/**
 * Runs the create chat flows opened from the chat list's create menu (FR60). One pop-up is
 * visible at a time; the others stay mounted but hidden, so a detour through New Contact
 * returns to the list or the group draft exactly as it was left.
 *
 * @param {string|null} flow CREATE_CHAT_FLOW to start, or null when nothing is open.
 * @param {Function} onClose Called once the flow ends, whichever way.
 */
const CreateChatHost = ({ flow, onClose }) => {
    const { t } = useLanguage();
    const dispatch = useDispatch();
    const [activeDialog, setActiveDialog] = useState(null);
    const [returnTo, setReturnTo] = useState(null);
    // A new key per flow remounts the pop-ups, so each starts blank.
    const [flowKey, setFlowKey] = useState(0);
    const [newContactKey, setNewContactKey] = useState(0);

    useEffect(() => {
        if (!flow) return;
        setActiveDialog(flow);
        setFlowKey(previous => previous + 1);
    }, [flow]);

    const handleClose = () => {
        setActiveDialog(null);
        onClose();
    };

    const handleOpenNewContact = from => {
        setReturnTo(from);
        setNewContactKey(previous => previous + 1);
        setActiveDialog(NEW_CONTACT);
    };

    // A pop-up that failed to render is closed; the create menu can open it again.
    const handleRenderError = () => {
        handleClose();
        toast.error(t(`${TOAST}.${T.SOMETHING_WENT_WRONG_RETRY}`));
    };

    const handleOpenChat = contact => {
        if (!isAppOnline()) {
            showOfflineError();
            return;
        }
        dispatch(chatResetMessage()).then(() => {
            dispatch(
                ActiveChatAction({
                    roster: toRosterEntry(contact),
                    recent: { chatType: 'chat' },
                    chatType: 'chat',
                    chatId: contact.userId,
                    chatJid: formatUserIdToJid(contact.userId),
                }),
            );
        });
        handleClose();
    };

    const handleGroupCreated = ({ groupId, groupName }) => {
        handleClose();
        dispatch(
            ActiveChatAction({
                chatId: groupId,
                chatJid: formatGroupIdToJid(groupId),
                recent: { chatType: 'groupchat', fromUserId: groupId },
                chatType: 'groupchat',
                roster: { groupId, groupName, nickName: groupName },
            }),
        );
        toast.success(t(`${TOAST}.${T.ACTION_SUCCESSFUL}`, { action: t('createChat.ACTION_GROUP_CREATION') }));
    };

    if (flowKey === 0) return null;

    return (
        <ErrorBoundary key={flowKey} fallback={null} context={{ screen: 'createChat' }} onError={handleRenderError}>
            <ContactListDialog
                open={activeDialog === CREATE_CHAT_FLOW.CONTACT_LIST}
                onClose={handleClose}
                onAddContact={() => handleOpenNewContact(CREATE_CHAT_FLOW.CONTACT_LIST)}
                onSelect={handleOpenChat}
            />
            <NewGroupDialog
                open={activeDialog === CREATE_CHAT_FLOW.NEW_GROUP}
                onClose={handleClose}
                onNewContact={() => handleOpenNewContact(CREATE_CHAT_FLOW.NEW_GROUP)}
                onCreated={handleGroupCreated}
            />
            <NewContactDialog
                key={newContactKey}
                open={activeDialog === NEW_CONTACT}
                onClose={() => setActiveDialog(returnTo)}
                onSaved={() => setActiveDialog(returnTo)}
            />
        </ErrorBoundary>
    );
};

CreateChatHost.propTypes = {
    flow: PropTypes.oneOf([...Object.values(CREATE_CHAT_FLOW), null]),
    onClose: PropTypes.func.isRequired,
};

export default CreateChatHost;
